/**
 * FILTER VOCABULARY BRIDGE
 *
 * Three vocabularies describe the same concepts in this codebase:
 *
 *   1. Browse filter option values — kebab-case slugs ('romance', 'pitch-black', 'hea')
 *   2. Book record fields          — Title Case strings ('Romance') or numbers (darknessLevel: 4)
 *   3. Saved reading preferences   — Title Case display names ('Fantasy', 'Spicy', 'HEA')
 *
 * Without a bridge, comparisons silently never match: 'romance' !== 'Romance',
 * and `book.spiceLevel === 'spicy'` compares a number to a string. This module
 * is the single place those three vocabularies are reconciled, so the filter
 * predicate and the preference sync both speak the same language.
 *
 * Level scales (spice and darkness) are ordered 1-5, so a slug maps to a
 * number and back regardless of which naming variant a surface happens to use.
 */

import { spiceLevels, darknessLevels, endingTypes } from '../data/bookTags'

/**
 * Content warnings arrive in two shapes: LitLaunch books use flat labels
 * ('Graphic Violence'), AfterTide books use grouped entries
 * ({ category, items: ['Graphic Violence', ...] }). Flatten both to labels.
 */
export type WarningEntry = string | { category?: string; items?: string[] }

export const flattenWarnings = (
  warnings: WarningEntry[] | undefined,
): string[] =>
  (warnings ?? []).flatMap((w) =>
    typeof w === 'string' ? [w] : Array.isArray(w?.items) ? w.items : [],
  )

/** Normalises any label or slug to a comparable kebab-case key. */
export const toSlug = (value: unknown): string =>
  String(value ?? '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

// ── Spice ────────────────────────────────────────────────────────────
// Browse uses the bookTags ids directly here, so one map covers both.
const SPICE_SLUG_TO_LEVEL: Record<string, number> = spiceLevels.reduce(
  (acc, entry) => {
    acc[entry.id] = entry.level
    acc[toSlug(entry.name)] = entry.level
    return acc
  },
  {} as Record<string, number>,
)

export const spiceSlugToLevel = (slug: string): number | undefined =>
  SPICE_SLUG_TO_LEVEL[toSlug(slug)]

// ── Darkness ─────────────────────────────────────────────────────────
// Browse's darkness options are ABBREVIATED variants of the bookTags names
// ('shadow' vs 'hint-of-shadow', 'void' vs 'the-void'), so both spellings are
// registered against the same level rather than renaming either surface.
const DARKNESS_BROWSE_ALIASES: Record<string, number> = {
  shadow: 1,
  grey: 2,
  gray: 2,
  abyss: 3,
  'abyss-dark': 3,
  dark: 3,
  'pitch-black': 4,
  void: 5,
}

const DARKNESS_SLUG_TO_LEVEL: Record<string, number> = darknessLevels.reduce(
  (acc, entry) => {
    acc[entry.id] = entry.level
    acc[toSlug(entry.name)] = entry.level
    return acc
  },
  { ...DARKNESS_BROWSE_ALIASES },
)

export const darknessSlugToLevel = (slug: string): number | undefined =>
  DARKNESS_SLUG_TO_LEVEL[toSlug(slug)]

/** Level -> the slug Browse's darkness filter uses, for preference sync. */
export const darknessLevelToBrowseSlug = (level: number): string | undefined =>
  (['shadow', 'grey', 'abyss', 'pitch-black', 'void'] as const)[level - 1]

/** Level -> the slug Browse's spice filter uses, for preference sync. */
export const spiceLevelToBrowseSlug = (level: number): string | undefined =>
  spiceLevels.find((entry) => entry.level === level)?.id

// ── Endings ──────────────────────────────────────────────────────────
const ENDING_SLUG_TO_NAME: Record<string, string> = endingTypes.reduce(
  (acc, entry) => {
    acc[entry.id] = entry.name
    acc[toSlug(entry.name)] = entry.name
    return acc
  },
  // Browse offers 'sad', which bookTags does not define; treat it as Tragic's
  // sibling so selecting it still behaves predictably.
  { sad: 'Sad' } as Record<string, string>,
)

export const endingSlugToName = (slug: string): string | undefined =>
  ENDING_SLUG_TO_NAME[toSlug(slug)]

// ── Matching helpers used by the discovery predicate ─────────────────

/** True when any selected genre slug matches any of the book's genre names. */
export const matchesGenreSlugs = (
  bookGenres: string[] | undefined,
  selectedSlugs: string[],
): boolean => {
  if (selectedSlugs.length === 0) return true
  if (!bookGenres || bookGenres.length === 0) return false
  const bookSlugs = bookGenres.map(toSlug)
  return selectedSlugs.some((slug) => {
    const target = toSlug(slug)
    // 'sci-fi' and 'science-fiction' are the same genre under two names.
    if (target === 'sci-fi' || target === 'science-fiction') {
      return bookSlugs.some((b) => b === 'sci-fi' || b === 'science-fiction')
    }
    if (target === 'contemporary') {
      return bookSlugs.some((b) => b.startsWith('contemporary'))
    }
    if (target === 'historical-fiction') {
      return bookSlugs.some((b) => b.startsWith('historical'))
    }
    return bookSlugs.includes(target)
  })
}

/** Matches selected slugs against a book's free-text tag/trope arrays. */
export const matchesTagSlugs = (
  bookValues: Array<string | undefined> | undefined,
  selectedSlugs: string[],
): boolean => {
  if (selectedSlugs.length === 0) return true
  if (!bookValues || bookValues.length === 0) return false
  const bookSlugs = bookValues
    .filter((value): value is string => Boolean(value))
    .map(toSlug)
  return selectedSlugs.some((slug) => bookSlugs.includes(toSlug(slug)))
}

/** Matches a numeric level field against selected level slugs. */
export const matchesLevelSlugs = (
  bookLevel: number | undefined,
  selectedSlugs: string[],
  resolver: (slug: string) => number | undefined,
): boolean => {
  if (selectedSlugs.length === 0) return true
  if (typeof bookLevel !== 'number') return false
  return selectedSlugs.some((slug) => resolver(slug) === bookLevel)
}

/** Matches a book's ending against selected ending slugs. */
export const matchesEndingSlugs = (
  bookEnding: string | undefined,
  selectedSlugs: string[],
): boolean => {
  if (selectedSlugs.length === 0) return true
  if (!bookEnding) return false
  return selectedSlugs.some((slug) => toSlug(slug) === toSlug(bookEnding))
}

/**
 * Matches a book's pairing/relationship type. The catalogue stores this as
 * `pairingType` ('MF'), with some records using `relationship`.
 */
export const matchesRelationshipSlugs = (
  book: Record<string, any>,
  selectedSlugs: string[],
): boolean => {
  if (selectedSlugs.length === 0) return true
  const candidates = [
    book.pairingType,
    book.relationship,
    ...(book.characterRelationships || []).flatMap((rel: any) => [
      rel.type,
      ...(rel.style || []),
    ]),
  ]
    .filter((value): value is string => Boolean(value))
    .map(toSlug)
  if (candidates.length === 0) return false
  return selectedSlugs.some((slug) => {
    const target = toSlug(slug)
    return candidates.some(
      (candidate) => candidate === target || candidate.includes(target),
    )
  })
}

/**
 * True when a book carries ANY excluded content warning. Exclusion is matched
 * on normalised slugs so 'Graphic Violence' and 'graphic-violence' agree.
 */
export const hasExcludedWarning = (
  bookWarnings: WarningEntry[] | undefined,
  excludedSlugs: string[],
): boolean => {
  if (excludedSlugs.length === 0) return false
  const bookSlugs = flattenWarnings(bookWarnings).map(toSlug)
  if (bookSlugs.length === 0) return false
  return excludedSlugs.some((slug) => bookSlugs.includes(toSlug(slug)))
}
